import { Grid } from '@utilitywarehouse/hearth-react';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';

import { EACAlert } from '@components/EACAlert';
import { MaskedInput } from '@components/form/MaskedInput';
import { useEnergyContext } from '@features/Energy/Provider.tsx';
import {
  hasEconomy7MeterType,
  hasElectricityService,
  hasGasService,
} from '@features/Energy/utils';
import { PredefinedConsumption } from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { EnergyDetailsFormValues } from '@src/types/energy';
import { formatNumber } from '@utils/format.ts';

import { CONSUMPTION_RADIO_GROUP_NAME } from '../constants';

interface KnownUsageFieldsProps {
  isLoading: boolean;
}

const KnownUsageFields = ({ isLoading }: KnownUsageFieldsProps) => {
  const {
    values,
    errors,
    touched,
    submitCount,
    handleBlur,
    handleChange,
    setFieldValue,
  } = useFormikContext<EnergyDetailsFormValues>();
  const { basketRaw } = useEnergyContext();

  const { fuelType, predefinedConsumption, meterType, isConsumptionPrefilled } =
    values;

  useEffect(() => {
    const touchedRadioGroup = Object.keys(touched).some((key) =>
      key.includes(CONSUMPTION_RADIO_GROUP_NAME)
    );

    if (
      touchedRadioGroup &&
      predefinedConsumption === PredefinedConsumption.Custom
    ) {
      setFieldValue('electricityUsageDay', '');
      setFieldValue('electricityUsageNight', '');
      setFieldValue('gasUsage', '');
    }
    // Should relay on RadioGroup interaction only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predefinedConsumption, setFieldValue]);

  const getFieldProps = useFieldProps<EnergyDetailsFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  if (!basketRaw?.energy) return null;

  const {
    electricityIndustryEstimateDay,
    electricityIndustryEstimateNight,
    gasIndustryEstimate,
  } = basketRaw!.energy.details;

  const hasElectricity = hasElectricityService(fuelType);
  const hasGas = hasGasService(fuelType);
  const isEcon7 = hasEconomy7MeterType(meterType);

  if (
    [
      PredefinedConsumption.Low,
      PredefinedConsumption.Medium,
      PredefinedConsumption.High,
    ].includes(predefinedConsumption)
  ) {
    return null;
  }

  const shouldShowElectricityEstimate = isEcon7
    ? electricityIndustryEstimateDay && electricityIndustryEstimateNight
    : electricityIndustryEstimateDay;

  const shouldShowGasEstimate =
    gasIndustryEstimate && shouldShowElectricityEstimate;

  return (
    <>
      <Grid
        asChild
        gap={{
          mobile: '0',
          tablet: '200',
        }}
        templateColumns={{
          mobile: '1fr',
          tablet: '1fr 1fr',
        }}
      >
        <fieldset className="energyDetailsForm__knownUsage">
          {hasElectricity && (
            <MaskedInput
              {...getFieldProps('electricityUsageDay')}
              label={`Electricity annual usage${isEcon7 ? ': Day' : ''} (kWh)`}
              mask="decimal"
              onChange={(value) => setFieldValue('electricityUsageDay', value)}
              helperText={
                shouldShowElectricityEstimate && electricityIndustryEstimateDay
                  ? 'Industry estimate: ' +
                    formatNumber(electricityIndustryEstimateDay)
                  : undefined
              }
              disabled={isLoading}
              required
            />
          )}
          {hasElectricity && isEcon7 && (
            <MaskedInput
              {...getFieldProps('electricityUsageNight')}
              label="Electricity annual usage: Night (kWh)"
              mask="decimal"
              onChange={(value) =>
                setFieldValue('electricityUsageNight', value)
              }
              helperText={
                shouldShowElectricityEstimate &&
                electricityIndustryEstimateNight
                  ? 'Industry estimate: ' +
                    formatNumber(electricityIndustryEstimateNight)
                  : undefined
              }
              disabled={isLoading}
              required
            />
          )}
          {hasGas && (
            <MaskedInput
              {...getFieldProps('gasUsage')}
              label="Gas annual usage (kWh)"
              mask="decimal"
              onChange={(value) => setFieldValue('gasUsage', value)}
              helperText={
                shouldShowGasEstimate && gasIndustryEstimate
                  ? 'Industry estimate: ' + formatNumber(gasIndustryEstimate)
                  : undefined
              }
              disabled={isLoading}
              required
            />
          )}
        </fieldset>
      </Grid>
      {isConsumptionPrefilled && <EACAlert />}
    </>
  );
};

export { KnownUsageFields };
